import { useEffect, useMemo, useState } from "react";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import { Alert, Box, Grid, MenuItem, Select, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import PageTitleIcon from "../components/PageTitleIcon";
import StatCard from "../components/dashboard/StatCard";
import { PilotLockedSection } from "../components/pilot/PilotLockedView";
import { getPilotDashboardStatistics } from "../services/dashboard.service";
import { useOrganizationStore } from "../stores/organization.store";
import type { PilotDashboardStatistics } from "../types/dashboard.type";
import {
  buildDashboardMonthOptions,
  getCurrentDashboardMonthValue,
} from "../utils/dashboardMonth.helpers";

type PilotMetric = {
  key: keyof PilotDashboardStatistics;
  titleKey: string;
  unit: string;
};

const PILOT_METRICS: PilotMetric[] = [
  { key: "waste_diversion_rate", titleKey: "pilot.metrics.wasteDiversionRate", unit: "%" },
  { key: "total_revenue", titleKey: "pilot.metrics.totalRevenue", unit: "SAR" },
  { key: "total_orders", titleKey: "pilot.metrics.totalOrders", unit: "" },
  { key: "completion_rate", titleKey: "pilot.metrics.completionRate", unit: "%" },
];

export default function PilotDashboard() {
  const { t } = useTranslation();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentDashboardMonthValue);
  const [statistics, setStatistics] = useState<PilotDashboardStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const availableMonths = useMemo(() => buildDashboardMonthOptions(), []);

  useEffect(() => {
    if (!selectedOrganization?.id) {
      setStatistics(null);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);
    void getPilotDashboardStatistics(selectedOrganization.id, selectedMonth)
      .then((result) => {
        if (active) setStatistics(result);
      })
      .catch((requestError: unknown) => {
        if (!active) return;
        setStatistics(null);
        setError(
          requestError instanceof Error
            ? requestError.message
            : t("pilot.dashboardLoadError"),
        );
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedOrganization?.id, selectedMonth, t]);

  return (
    <Box sx={{ p: { xs: 0, md: 1 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PageTitleIcon icon={<DashboardOutlinedIcon />} />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {t("dashboard.title")}
            </Typography>
            <Typography color="text.secondary">
              {selectedOrganization?.name ?? t("dashboard.selectOrganization")}
            </Typography>
          </Box>
        </Box>
        <Select
          size="small"
          value={selectedMonth}
          onChange={(event) => setSelectedMonth(event.target.value)}
          sx={{ minWidth: 200, borderRadius: 3 }}
          aria-label={t("pilot.month")}
        >
          {availableMonths.map((month) => (
            <MenuItem key={month.value} value={month.value}>
              {month.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {!selectedOrganization && <Alert severity="info">{t("dashboard.selectOrganization")}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {PILOT_METRICS.map((metric) => (
          <Grid item xs={12} sm={6} lg={3} key={metric.key}>
            {isLoading ? (
              <Skeleton variant="rounded" height={150} sx={{ borderRadius: 3 }} />
            ) : (
              <StatCard
                title={t(metric.titleKey)}
                value={Number(statistics?.[metric.key] ?? 0)}
                unit={metric.unit}
                trend={0}
                hideTrend
              />
            )}
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2.5 }}>
        <PilotLockedSection
          minHeight={380}
          label={t("dashboard.sections.charts")}
          preview="chart"
        />
        <PilotLockedSection
          minHeight={380}
          label={t("pilot.chartsAndMap")}
          preview="map"
        />
      </Box>
    </Box>
  );
}
